import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  LayoutDashboard, ListChecks, Users, LogOut, Home, Menu, X, ChevronLeft, ChevronRight
} from "lucide-react";

const NAV_ITEMS = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Tableau de bord" },
  { to: "/liste-attente", icon: ListChecks, label: "Liste d'attente" },
  { to: "/utilisateurs", icon: Users, label: "Utilisateurs", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebarWidth = collapsed ? "w-14" : "w-56";

  const SidebarContent = ({ isMobile = false }) => (
    <div className="flex flex-col h-full">
      {/* Logo + collapse button */}
      <div className={`flex items-center border-b border-slate-100 h-14 flex-shrink-0 ${collapsed && !isMobile ? "justify-center px-2" : "px-4 gap-2.5"}`}>
        <div className="w-7 h-7 bg-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0">
          <Home className="w-4 h-4 text-white" />
        </div>
        {(!collapsed || isMobile) && (
          <span className="text-base font-bold text-slate-900 truncate" style={{ fontFamily: "Outfit, sans-serif" }}>
            ListeAttente
          </span>
        )}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(c => !c)}
            className={`ml-auto p-1 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors flex-shrink-0 ${collapsed ? "hidden" : ""}`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className={`flex-1 py-3 space-y-0.5 ${collapsed && !isMobile ? "px-1.5" : "px-2"}`}>
        {NAV_ITEMS.filter(item => !item.adminOnly || user?.role === "admin").map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            onClick={() => isMobile && setMobileOpen(false)}
            title={collapsed && !isMobile ? label : undefined}
            className={({ isActive }) =>
              `flex items-center rounded-lg text-sm font-medium transition-all duration-150 ${
                collapsed && !isMobile ? "justify-center w-10 h-10 mx-auto" : "gap-3 px-3 py-2.5"
              } ${
                isActive
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
              }`
            }
            data-testid={`nav-${to.replace("/", "")}`}
          >
            <Icon className="w-4 h-4 flex-shrink-0" />
            {(!collapsed || isMobile) && <span>{label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* User footer */}
      <div className={`border-t border-slate-100 py-3 ${collapsed && !isMobile ? "px-1.5" : "px-3"}`}>
        {(!collapsed || isMobile) && (
          <div className="flex items-center gap-2.5 mb-2 px-1">
            <div className="w-7 h-7 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-bold text-indigo-700">{user?.name?.[0]?.toUpperCase() || "?"}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-400">{user?.role === "admin" ? "Admin" : "Opérateur"}</p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="logout-btn"
          title={collapsed && !isMobile ? "Déconnexion" : undefined}
          className={`flex items-center text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors ${
            collapsed && !isMobile ? "justify-center w-10 h-10 mx-auto" : "gap-2 px-3 py-2 w-full"
          }`}
        >
          <LogOut className="w-4 h-4" />
          {(!collapsed || isMobile) && <span>Déconnexion</span>}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex" style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Desktop Sidebar */}
      <aside className={`hidden lg:flex ${sidebarWidth} bg-white border-r border-slate-200 flex-col fixed h-full z-30 transition-all duration-200`}>
        <SidebarContent />
      </aside>

      {/* Collapse toggle button (visible en mode replié) */}
      {collapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className="hidden lg:flex fixed left-14 top-4 z-40 w-5 h-8 bg-white border border-slate-200 rounded-r-lg items-center justify-center text-slate-400 hover:text-indigo-600 shadow-sm"
        >
          <ChevronRight className="w-3 h-3" />
        </button>
      )}

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40 flex">
          <div className="fixed inset-0 bg-slate-900/50" onClick={() => setMobileOpen(false)} />
          <div className="relative w-56 bg-white h-full z-50 shadow-xl">
            <SidebarContent isMobile />
          </div>
        </div>
      )}

      {/* Main */}
      <div className={`flex-1 flex flex-col min-h-screen transition-all duration-200 ${collapsed ? "lg:ml-14" : "lg:ml-56"}`}>
        {/* Mobile header */}
        <header className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-20">
          <button onClick={() => setMobileOpen(true)} className="p-1.5 rounded-md hover:bg-slate-100">
            <Menu className="w-5 h-5 text-slate-600" />
          </button>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center">
              <Home className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="font-bold text-slate-900 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
              ListeAttente
            </span>
          </div>
        </header>

        <main className="flex-1 p-4 lg:p-6">
          {children}
        </main>
      </div>
    </div>
  );
}
